import React from 'react';
import { View, Text, StyleSheet, ViewStyle } from 'react-native';
import { useTheme, SPACING, BORDER_RADIUS, TYPOGRAPHY } from '../../theme';
import {
  ReadinessLevel,
  READINESS_LABELS,
  getReadinessColor,
  getReadinessLevelFromCounts,
  getReadinessLevelFromPercent,
} from '../../utils/readiness';

interface ReadinessMeterProps {
  completedCount?: number;
  totalCount?: number;
  /** Percent-only mode for callers that only have a denormalized percent
      (e.g. structure claimSummary). Ignored when counts are provided; the
      item-count sub-line is omitted. */
  percent?: number;
  style?: ViewStyle;
  testID?: string;
  /** testID for the percent text itself (audit-interactions reads its
   *  textContent to assert the figure moves when an item is toggled). */
  percentTestID?: string;
  /** The numbers aren't in yet. Draws the card at its loaded size with
   *  blank bars where the figures go, so a page never flashes "0%" before
   *  the checklist and its progress arrive. The bars are static on purpose:
   *  the visual audit can't freeze an RN Animated shimmer. */
  loading?: boolean;
}

/**
 * The Plaud-grammar readiness block, replacing the gradient hero: a label
 * row ("Readiness · In progress" with the level word in the level color), a
 * large LIGHT percent, a thin 4px meter in the level color, and the item
 * count beneath. Quiet surface card; no gradient, no glow.
 */
export function ReadinessMeter({
  completedCount,
  totalCount,
  percent: percentProp,
  style,
  testID,
  percentTestID,
  loading,
}: ReadinessMeterProps) {
  const { colors } = useTheme();

  if (loading) {
    return (
      <View
        style={[styles.card, { backgroundColor: colors.surface }, style]}
        testID="readiness-meter-loading"
        accessibilityLabel="Loading wildfire readiness">
        <View style={styles.row}>
          <Text style={[styles.label, { color: colors.text }]}>Readiness</Text>
          <View style={[styles.percentBlank, { backgroundColor: colors.skeletonBase }]} />
        </View>
        <View style={[styles.track, { backgroundColor: colors.border }]} />
        <View style={[styles.subBlank, { backgroundColor: colors.skeletonBase }]} />
      </View>
    );
  }

  const counts =
    completedCount != null && totalCount != null
      ? { completed: completedCount, total: totalCount }
      : null;
  const hasChecklist = counts != null && counts.total > 0;
  const percent = counts
    ? hasChecklist
      ? Math.round((counts.completed / counts.total) * 100)
      : 0
    : Math.round(percentProp ?? 0);
  const level: ReadinessLevel = counts
    ? getReadinessLevelFromCounts(counts.completed, counts.total)
    : getReadinessLevelFromPercent(percent);
  const levelColor = getReadinessColor(level, colors) ?? colors.textTertiary;
  // The neutral stone misses AA by a hair on the surface card (4.47:1), so
  // the "Not started" word renders in textSecondary; the meter fill keeps
  // the neutral color.
  const levelWordColor = level === 'none' ? colors.textSecondary : levelColor;

  return (
    <View
      style={[styles.card, { backgroundColor: colors.surface }, style]}
      testID={testID}
      accessibilityLabel={`Wildfire readiness, ${percent} percent complete`}>
      <View style={styles.row}>
        <Text style={[styles.label, { color: colors.text }]}>
          Readiness · <Text style={{ color: levelWordColor }}>{READINESS_LABELS[level]}</Text>
        </Text>
        <Text testID={percentTestID} style={[styles.percent, { color: colors.text }]}>
          {percent}%
        </Text>
      </View>
      <View style={[styles.track, { backgroundColor: colors.border }]}>
        <View
          style={[
            styles.fill,
            {
              backgroundColor: levelColor,
              width: `${Math.max(0, Math.min(100, percent))}%`,
            },
          ]}
        />
      </View>
      {counts && (
        <Text style={[styles.sub, { color: colors.textSecondary }]}>
          {hasChecklist
            ? `${counts.completed} of ${counts.total} checklist items complete`
            : 'Start your readiness checklist'}
        </Text>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  card: {
    borderRadius: BORDER_RADIUS.lg,
    paddingVertical: SPACING.smd,
    paddingHorizontal: SPACING.md,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'baseline',
    justifyContent: 'space-between',
  },
  label: {
    ...TYPOGRAPHY.label,
  },
  percent: {
    ...TYPOGRAPHY.numeralLight,
  },
  track: {
    height: 4,
    borderRadius: 2,
    overflow: 'hidden',
    marginTop: SPACING.sm,
  },
  fill: {
    height: '100%',
    borderRadius: 2,
  },
  sub: {
    ...TYPOGRAPHY.metadata,
    marginTop: SPACING.xs + 3,
  },
  // Sized to the numeralLight and metadata lines they stand in for.
  percentBlank: {
    width: 56,
    height: TYPOGRAPHY.numeralLight.lineHeight,
    borderRadius: BORDER_RADIUS.sm,
  },
  subBlank: {
    width: '45%',
    height: TYPOGRAPHY.metadata.lineHeight,
    borderRadius: BORDER_RADIUS.sm,
    marginTop: SPACING.xs + 3,
  },
});
